/* The pieces every strategy-aware screen reuses.

   The rule this file exists to enforce: an initiative's numbers are rendered
   from the server's rollup and nowhere else. `RollupCells` takes a `Rollup`
   and has no access to the portfolio arrays, so there is no way for a caller
   to pass it a count it worked out itself. The same goes for the gap badges --
   the conditions live in store/strategy.py, and this file only knows how to
   name them. */
import type { ReactNode } from "react";

import type { Bet, Initiative, InitiativeRef, Rollup } from "../api";
import { href } from "../route";
import { useStore } from "../store";

export const NO_INITIATIVE = "__none__";

/* The short code, wherever an item appears in a list. Deliberately small and
   monospaced: it sits beside a service name and must not compete with it. */
export function InitiativeChip({
  initiative,
  source,
}: {
  initiative: InitiativeRef | null;
  source?: "direct" | "inherited";
}) {
  if (!initiative) return null;
  return (
    <a
      className={`icode${source === "inherited" ? " inherited" : ""}`}
      href={href.initiative(initiative.id)}
      title={
        source === "inherited"
          ? `${initiative.name} — inherited from the service or asset this task is on`
          : initiative.name
      }
      onClick={(e) => e.stopPropagation()}
    >
      {initiative.code}
    </a>
  );
}

/* Which bets sit behind an initiative. Resolved from the store rather than
   served on every row: it is a name lookup, not a number, so there is nothing
   here that can disagree with the server. */
export function BetChips({ betIds, exclude }: { betIds: number[]; exclude?: number }) {
  const { bets } = useStore();
  const shown = betIds.filter((id) => id !== exclude);
  if (shown.length === 0) return null;
  return (
    <>
      {shown.map((id) => {
        const bet = bets.find((b) => b.id === id);
        if (!bet) return null;
        return (
          <a key={id} className="bcode" href={href.bet(id)} title={bet.name}>
            {bet.code}
          </a>
        );
      })}
    </>
  );
}

const GAP_TEXT: Record<string, string> = {
  no_bet: "not linked to a bet",
  no_work: "nothing behind it",
  all_services_idea: "active, but every service is still at Idea",
  no_initiatives: "no initiatives",
};

export function GapBadges({ gaps }: { gaps: string[] | undefined }) {
  if (!gaps || gaps.length === 0) return null;
  return (
    <>
      {gaps.map((gap) => (
        <span key={gap} className="gap">
          {GAP_TEXT[gap] ?? gap}
        </span>
      ))}
    </>
  );
}

export function statusTone(status: string): "good" | "warn" | "bad" | "" {
  if (status === "active") return "good";
  if (status === "on hold" || status === "paused" || status === "exploring") return "warn";
  if (status === "dropped") return "bad";
  return "";
}

/* The rollup, rendered the one way it is ever rendered. Services split active
   against Idea; End of life counted in neither, because one has not started
   and the other has stopped. */
export function RollupCells({ rollup }: { rollup: Rollup | undefined }) {
  const r = rollup;
  if (!r) return null;
  return (
    <>
      <td className="num">
        {r.services_active}
        {r.services_idea > 0 && <span className="muted"> + {r.services_idea} idea</span>}
        {r.services_retired > 0 && <span className="muted"> · {r.services_retired} eol</span>}
      </td>
      <td className="num">{r.assets || <span className="muted">—</span>}</td>
      <td className="num">
        {r.tasks === 0 ? (
          <span className="muted">—</span>
        ) : (
          <>
            {r.tasks_open + r.tasks_in_progress}
            <span className="muted"> of {r.tasks}</span>
            {r.tasks_overdue > 0 && <span className="overdue"> {r.tasks_overdue} overdue</span>}
          </>
        )}
      </td>
      <td className="num">
        {r.maturity === null ? <span className="muted">—</span> : `${Math.round(r.maturity)}%`}
      </td>
    </>
  );
}

export function RollupHeaders() {
  return (
    <>
      <th className="num" title="Services under way, and how many are still at Idea">
        Services
      </th>
      <th className="num">Assets</th>
      <th className="num" title="Tasks still open, of all tasks that resolve to this initiative">
        Tasks
      </th>
      <th className="num" title="Average maturity of its services and assets">
        Maturity
      </th>
    </>
  );
}

/* Choosing an initiative, with the one warning that matters: an object holds
   exactly one, so picking a new one is a move away from the old one, and the
   person doing it should be told which. */
export function InitiativePicker({
  value,
  onChange,
  disabled,
  allowNone = true,
  currentLabel,
}: {
  value: number | null;
  onChange: (next: number | null) => void;
  disabled?: boolean;
  allowNone?: boolean;
  currentLabel?: string;
}) {
  const { initiatives } = useStore();
  const current = initiatives.find((i) => i.id === value);

  return (
    <select
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => {
        const next = e.target.value === "" ? null : Number(e.target.value);
        if (next !== null && current && current.id !== next) {
          const to = initiatives.find((i) => i.id === next);
          const what = currentLabel ? `"${currentLabel}" is` : "This is";
          if (
            !confirm(
              `${what} currently in ${current.code} ${current.name}.\n\n` +
                `This will move it to ${to?.code} ${to?.name}.`,
            )
          ) {
            return;
          }
        }
        onChange(next);
      }}
    >
      {allowNone && <option value="">No initiative</option>}
      {initiatives.map((i) => (
        <option key={i.id} value={i.id}>
          {i.code} — {i.name}
        </option>
      ))}
    </select>
  );
}

export function QuarterSelect({
  value,
  onChange,
  quarters,
}: {
  value: string | null;
  onChange: (next: string | null) => void;
  quarters: string[];
}) {
  /* Five years either side of now is more than a 2-4 quarter initiative needs
     and less than a free-text box invites. */
  const year = new Date().getFullYear();
  const options: string[] = [];
  for (let y = year - 1; y <= year + 4; y++) for (const q of quarters) options.push(`${y}-${q}`);
  return (
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value || null)}>
      <option value="">—</option>
      {options.map((o) => (
        <option key={o}>{o}</option>
      ))}
    </select>
  );
}

export function initiativeLabel(i: Initiative | undefined): string {
  return i ? `${i.code} — ${i.name}` : "";
}

export function betLabel(b: Bet | undefined): string {
  return b ? `${b.code} — ${b.name}` : "";
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="note">{children}</p>;
}
